import type { QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { format, isValid, parse, parseISO } from "date-fns";

const STRICT_YYYY_MM_DD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a FHIR `valueDate` as a local calendar date (avoids the UTC-midnight day-shift
 * that `parseISO` would introduce). Requires an exact `yyyy-MM-dd` string — an ISO
 * datetime, a non-zero-padded date, or surrounding whitespace all return `null` rather
 * than being silently truncated or loosely matched.
 */
export function parseQuestionnaireResponseLocalValueDate(
  valueDate: string | undefined,
): Date | null {
  if (!valueDate || !STRICT_YYYY_MM_DD_PATTERN.test(valueDate)) {
    return null;
  }
  const parsed = parse(valueDate, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
}

/**
 * Finds a direct child item by linkId (labor flowsheet responses use shallow nesting).
 * Does not search grandchildren — call again on `item.item` for deeper levels.
 */
export function findQuestionnaireResponseItem(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem | undefined {
  return items?.find((item) => item.linkId === linkId);
}

/**
 * Reads a decimal answer as a string for NumberInput/form hydration.
 * Missing values return `""` (empty field), including when the item is undefined.
 */
export function readQuestionnaireDecimalAnswer(
  item: QuestionnaireResponseItem | undefined,
): string {
  const value = item?.answer?.[0]?.valueDecimal;
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

/**
 * Reads a string answer for text fields. Missing values return `""`.
 */
export function readQuestionnaireStringAnswer(item: QuestionnaireResponseItem | undefined): string {
  return item?.answer?.[0]?.valueString ?? "";
}

/**
 * Reads a coding answer's `code` for fields that store code only (e.g. listening device).
 * Missing/invalid coding returns `null` (no selection). For NativeSelect values that need the full
 * Coding JSON, use `readCodingAnswerAsNativeSelectValue` in `labor-flowsheet-coding-select.ts`.
 */
export function readQuestionnaireCodingCodeAnswer(
  item: QuestionnaireResponseItem | undefined,
): string | null {
  const coding = item?.answer?.[0]?.valueCoding;
  if (!coding?.code) {
    return null;
  }
  return coding.code;
}

/**
 * Reads a standalone `dateTime`-typed answer (not a split date/time group) as a `Date`.
 * Missing or unparseable values return `null`.
 */
export function readQuestionnaireDateTimeAnswer(
  item: QuestionnaireResponseItem | undefined,
): Date | null {
  const value = item?.answer?.[0]?.valueDateTime;
  if (!value) {
    return null;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/**
 * Reads a `date`-typed answer as a local calendar date (avoids timezone day shifts).
 * Only exact `yyyy-MM-dd` values are accepted; missing or malformed values return `null`.
 */
export function readQuestionnaireLocalDateAnswer(
  item: QuestionnaireResponseItem | undefined,
): Date | null {
  const value = item?.answer?.[0]?.valueDate;
  if (!value) {
    return null;
  }
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  // `parse` alone accepts non-zero-padded values (e.g. "2026-8-10"), silently normalizing them -
  // formatting back and comparing to the original catches that without a separate regex.
  if (!isValid(parsed) || format(parsed, "yyyy-MM-dd") !== value) {
    return null;
  }
  return parsed;
}

/**
 * Reads signature-initial answers as practitioner ids. Empty strings are dropped; missing answers
 * return `[]`.
 */
export function readQuestionnaireSignatureInitials(
  item: QuestionnaireResponseItem | undefined,
): string[] {
  return (
    item?.answer
      ?.map((answer) => answer.valueString)
      .filter((value): value is string => typeof value === "string" && value.length > 0) ?? []
  );
}
